import { addMinutes } from 'date-fns';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Grid } from 'src/components/Grid';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { FormDateTimePicker } from 'src/shared/Forms/Fields/FormDateTimePicker';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { BaseSelectOption } from 'src/shared/Forms/Selects/BaseSelect';
import { FormLocationSelect } from 'src/shared/Forms/Selects/LocationSelect';
import { ServiceSelect } from 'src/shared/Forms/Selects/ServiceSelect';

import { EditEventFormValues } from './_types';

import type { Service } from 'src/models/service/_types';
import type { Identifier } from 'src/types/utilityTypes';

interface GeneralSettingsSectionProps {
  getServiceById: (serviceId: Identifier | null) => Service | undefined;
  isEditing: boolean;
  isRecurring?: boolean;
}

export const GeneralSettingsSection: FC<GeneralSettingsSectionProps> = ({
  getServiceById,
  isEditing,
  isRecurring = false,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'calendar.editEventDialog.generalSettings' });
  const { values, setFieldValue, errors, touched } =
    useFormikContext<EditEventFormValues>();

  const selectedService = getServiceById(values.serviceId);
  const isGroupClass = selectedService?.serviceType === 'group_class';

  const handleServiceChange: {
    (value: Identifier | BaseSelectOption | null): void;
    (value: Identifier[] | BaseSelectOption[]): void;
  } = (value) => {
    // Single select - extract the ID
    const serviceId =
      value && typeof value === 'object' && !Array.isArray(value)
        ? (value as BaseSelectOption).id
        : (value as Identifier | null);

    setFieldValue('serviceId', serviceId ?? null);

    // Update end time based on service's default duration
    if (serviceId) {
      const service = getServiceById(serviceId);
      if (service?.defaultTime) {
        setFieldValue(
          'endTime',
          addMinutes(values.startTime, service.defaultTime)
        );
      }
    }
  };

  const handleStartTimeChange = (date: Date | null) => {
    if (!date) return;

    // Update end time based on current service's default duration
    const service = getServiceById(values.serviceId);
    const duration = service?.defaultTime || 60;
    setFieldValue('endTime', addMinutes(date, duration));
  };

  return (
    <Box
      component="fieldset"
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        m: 0,
      }}
    >
      <Typography variant="h6" component="legend">
        {t('title')}
      </Typography>

      <Stack spacing={3}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ServiceSelect
              name="serviceId"
              label={t('service')}
              required
              value={values.serviceId}
              onChange={handleServiceChange}
              error={touched.serviceId && !!errors.serviceId}
              helperText={
                touched.serviceId && errors.serviceId
                  ? String(errors.serviceId)
                  : undefined
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormLocationSelect name="locationId" label={t('location')} />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormDateTimePicker
              name="startTime"
              label={t('startTime')}
              required
              fullWidth
              onChange={(date) => handleStartTimeChange(date)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormDateTimePicker
              name="endTime"
              label={t('endTime')}
              required
              fullWidth
            />
          </Grid>
        </Grid>

        {isGroupClass && isEditing && (
          <FormTextField
            name="maxVisits"
            label={t('maxSignups')}
            type="number"
            placeholder={t('unlimitedPlaceholder')}
            helperText={
              isRecurring ? t('onlyAppliesToThisSession') : undefined
            }
          />
        )}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormTextField
              name="description"
              label={t('description')}
              multiline
              rows={3}
              placeholder={t('descriptionPlaceholder')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormTextField
              name="notes"
              label={t('notes')}
              multiline
              rows={3}
              placeholder={t('notesPlaceholder')}
            />
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
